import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { hasAnalyticsCookieConsent } from "@/components/CookieConsent";

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

export const initSentry = () => {
  if (!SENTRY_DSN && import.meta.env.MODE !== "production") {
    // Sentry disabled in development - no logging needed
    return;
  }

  // Don't initialize without user consent
  if (!hasAnalyticsCookieConsent()) {
    // Sentry disabled: No analytics consent - no logging needed
    return;
  }

  const hasConsent = hasAnalyticsCookieConsent();

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_APP_VERSION || "dev",
    integrations: [
      Sentry.browserTracingIntegration(),
      // Only enable session replays if user consented to analytics cookies
      ...(hasConsent ? [Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      })] : []),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],

    tracesSampleRate: import.meta.env.MODE === "production" ? 0.1 : 1.0,
    // Only sample replays if user has consented
    replaysSessionSampleRate: hasConsent ? 0.1 : 0,
    replaysOnErrorSampleRate: hasConsent ? 1.0 : 0,

    // Performance monitoring thresholds
    beforeSendTransaction(transaction) {
      // Capture slow transactions for alerting
      if (transaction.measurements?.["mark.fcp"]?.value && 
          transaction.measurements["mark.fcp"].value > 3000) {
        transaction.tags = {
          ...transaction.tags,
          slow_page_load: "true",
          severity: "warning"
        };
      }

      // Tag slow API calls - check duration
      if (transaction.timestamp && transaction.start_timestamp &&
          (transaction.timestamp - transaction.start_timestamp) > 1) {
        transaction.tags = {
          ...transaction.tags,
          slow_api_call: "true",
          severity: "warning"
        };
      }

      return transaction;
    },

    beforeSend(event, hint) {
      if (event.exception) {
        const error = hint.originalException;
        
        // Filter out network errors in non-production
        if (import.meta.env.MODE !== "production" && error instanceof Error) {
          if (error.message.includes("NetworkError") || 
              error.message.includes("Failed to fetch")) {
            return null;
          }
        }

        // Tag critical errors for immediate alerting
        if (error instanceof Error) {
          // Database connection failures - CRITICAL
          if (error.message.includes("database") || 
              error.message.includes("connection") ||
              error.message.includes("PGRST")) {
            event.level = "error";
            event.tags = {
              ...event.tags,
              alert_priority: "critical",
              error_category: "database"
            };
          }

          // Authentication failures - CRITICAL
          if (error.message.includes("auth") || 
              error.message.includes("unauthorized") ||
              error.message.includes("JWT")) {
            event.level = "error";
            event.tags = {
              ...event.tags,
              alert_priority: "high",
              error_category: "authentication"
            };
          }

          // Payment processing errors - CRITICAL (when Stripe is added)
          if (error.message.includes("payment") || 
              error.message.includes("stripe") ||
              error.message.includes("billing")) {
            event.level = "error";
            event.tags = {
              ...event.tags,
              alert_priority: "critical",
              error_category: "payment"
            };
          }
        }
      }

      // Remove IP addresses for privacy
      if (event.user) {
        event.user.ip_address = undefined;
      }

      return event;
    },

    ignoreErrors: [
      "top.GLOBALS",
      "originalCreateNotification",
      "canvas.contentDocument",
      "MyApp_RemoveAllHighlights",
      "Can't find variable: ZiteReader",
      "jigsaw is not defined",
      "ComboSearch is not defined",
      "fb_xd_fragment",
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      "ChunkLoadError",
      "Loading chunk",
    ],

    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /graph\.facebook\.com/i,
      /connect\.facebook\.net\/en_US\/all\.js/i,
    ],
  });
};

export const setSentryUser = (user: { id: string; email: string } | null) => {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
    });
  } else {
    Sentry.setUser(null);
  }
};

export const captureException = (error: Error, context?: Record<string, any>) => {
  Sentry.captureException(error, {
    contexts: context ? { custom: context } : undefined,
  });
};

export const captureMessage = (message: string, level: Sentry.SeverityLevel = "info") => {
  Sentry.captureMessage(message, level);
};

export const addBreadcrumb = (message: string, data?: Record<string, any>) => {
  Sentry.addBreadcrumb({
    message,
    data,
    level: "info",
  });
};

export const startTransaction = (name: string, op: string) => {
  return Sentry.startSpan({ name, op }, (span) => span);
};

export const withSentry = <T extends (...args: any[]) => any>(fn: T): T => {
  return ((...args: Parameters<T>) => {
    try {
      const result = fn(...args);
      
      if (result instanceof Promise) {
        return result.catch((error) => {
          captureException(error);
          throw error;
        });
      }
      
      return result;
    } catch (error) {
      captureException(error as Error);
      throw error;
    }
  }) as T;
};
